import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useLossPercentagesReadContext } from '@context/LossPercentagesContext'
import { useModal } from '@context/ModalsContext'
import { useProductReadContext, useProductWriteContext } from '@context/ProductContext'

import { isTickmillPartner } from '@utils/companyName.utils'
import { CommonPaths, navigateBasedOnProductType } from '@utils/path'

import { TickmillProductType } from '@model/TickmillProductType'

import { FormTemplate } from '@global/templates/FormTemplate'

import { Loading } from '@shared/components/Loading/Loading'
import { ActionModal } from '@shared/modals/Action.modal'
import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'
import { RiskWarningModal } from '@shared/modals/RiskWarning.modal'

import {
  YearlyKycUpdateDetailsForm,
  YearlyKycUpdateDetailsFormValues,
} from '../../Profile/YearlyKycUpdateDetails/YearlyKycUpdateDetailsForm/YearlyKycUpdateDetailsForm'
import {
  useYearlyKycUpdateDetails,
  useYearlyKycUpdateDetailsMutate,
} from '../../Profile/YearlyKycUpdateDetails/YearlyKycUpdateDetailsPage'
import { ProductRiskWarning } from '../ProductRiskWarning'

import styles from './ProductPersonalDetails.module.scss'

export const ProductPersonalDetailsPage: React.FC = () => {
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const { percentages: lossPercentage } = useLossPercentagesReadContext()

  const { product } = useProductReadContext()
  const { changeProductContext } = useProductWriteContext()
  const { isDefaultCFDProductType } = useProductReadContext()

  const isCFDProduct = isDefaultCFDProductType()
  const isPartner = isTickmillPartner(account)
  const { countries, accountKycTest, tinUnavailableReasons, isLoading } =
    useYearlyKycUpdateDetails()

  const newProductRegistrationRequirement = true

  const mutation = useYearlyKycUpdateDetailsMutate(newProductRegistrationRequirement, {
    onSuccess: () => navigate(CommonPaths.ProductKycSuccess, { replace: true }),
  })

  const handleSubmitForm = async (values: YearlyKycUpdateDetailsFormValues) => {
    await mutation.mutate(account, accountKycTest, values, alreadyUploadedModal.open)
  }

  const { t } = useTranslation()

  const handleConfirmExit = () => {
    changeProductContext(isCFDProduct ? TickmillProductType.ETD : TickmillProductType.CFD)
    navigateBasedOnProductType(isCFDProduct, navigate, isPartner)
  }
  const cancelModal = useModal(ActionModal, { onConfirm: handleConfirmExit })
  const alreadyUploadedModal = useModal(AlreadyUploadedModal)
  const riskWarningModal = useModal(RiskWarningModal, {
    selectedProductIds: [product],
    risk: lossPercentage?.percentage,
  })

  return (
    <Loading isLoading={isLoading || mutation.isLoading} showLoadingIcon>
      <FormTemplate
        title={t('Profile.Are your details up to date?')}
        renderIcon={<ProductRiskWarning onClick={riskWarningModal.open} />}
        goBack={cancelModal.open}
        contentClassName={classNames(styles.container, { [styles.boxLess]: !mutation.isSuccess })}
      >
        <YearlyKycUpdateDetailsForm
          account={account}
          countries={countries}
          accountKycTest={accountKycTest}
          onSubmit={handleSubmitForm}
          tinUnavailableReasons={tinUnavailableReasons}
          productRegistrationRequirement={newProductRegistrationRequirement}
        />
      </FormTemplate>
    </Loading>
  )
}
